/** Protocol message for passenger form submit (non-empty for API coerce). */
export const CVAC_PASSENGER_FORM_SUBMIT = "__cvac_passenger_form_submit__";

/**
 * Resolve the `reply` object from a customer-chat turn response body.
 * Supports both `data.payload` (inner) and full `data` ({ status, payload: { reply } }).
 * @param {unknown} apiPayload
 * @returns {Record<string, unknown>}
 */
export function resolveCustomerChatTurnReply(apiPayload) {
  if (!apiPayload || typeof apiPayload !== "object") return {};
  const outer = apiPayload.reply;
  if (outer && typeof outer === "object") return outer;
  const inner = apiPayload.payload?.reply;
  if (inner && typeof inner === "object") return inner;
  return {};
}

/**
 * Extract passenger_form from a turn API payload.
 * Checks `reply.passenger_form`, nested `payload.reply.passenger_form`, camelCase, tool_output.
 * @param {unknown} apiPayload — `response.data.payload` or full `response.data`
 * @returns {{ form: object | null, passengersCount: number }}
 */
export function normalizePassengerFormFromReply(apiPayload) {
  const pf =
    apiPayload?.reply?.passenger_form ||
    apiPayload?.reply?.passengerForm ||
    apiPayload?.payload?.reply?.passenger_form ||
    apiPayload?.payload?.reply?.passengerForm ||
    apiPayload?.payload?.passenger_form ||
    apiPayload?.payload?.passengerForm ||
    null;

  const reply = resolveCustomerChatTurnReply(apiPayload);
  const pfFromReply =
    pf ||
    reply.passenger_form ||
    reply.passengerForm ||
    reply.tool_output?.passenger_form ||
    reply.tool_output?.passengerForm ||
    reply.payload?.passenger_form ||
    reply.payload?.passengerForm ||
    (apiPayload && typeof apiPayload === "object" ? apiPayload.passenger_form : null) ||
    (apiPayload && typeof apiPayload === "object" ? apiPayload.passengerForm : null) ||
    null;

  const raw = pfFromReply && typeof pfFromReply === "object" ? pfFromReply : null;
  if (!raw) {
    return { form: null, passengersCount: 0 };
  }
  let passengers = raw.passengers;
  if (!Array.isArray(passengers)) {
    passengers = [];
  }
  passengers = passengers.filter((p) => p && typeof p === "object");
  if (passengers.length === 0) {
    return { form: null, passengersCount: 0 };
  }
  const form = { ...raw, passengers };
  return {
    form,
    passengersCount: passengers.length,
  };
}

/**
 * @param {unknown} rooms
 * @param {unknown} currentRoomsAndPaxInfo
 * @returns {unknown}
 */
export function mergeChatPassengerRoomsIntoPrev(rooms, currentRoomsAndPaxInfo) {
  if (!rooms?.[0]) return currentRoomsAndPaxInfo;
  const r0 = rooms[0];
  const base = Array.isArray(currentRoomsAndPaxInfo) && currentRoomsAndPaxInfo.length
    ? JSON.parse(JSON.stringify(currentRoomsAndPaxInfo))
    : [
        {
          adults: [],
          kids: [],
          infants: [],
          room: { roomSet: "", roomType: "", "@seqNum": 1 },
        },
      ];
  const cur0 = base[0] || {};
  base[0] = {
    ...cur0,
    adults: Array.isArray(r0.adults) ? r0.adults : [],
    kids: Array.isArray(r0.kids) ? r0.kids : [],
    infants: Array.isArray(r0.infants) ? r0.infants : [],
    room: {
      ...(cur0.room && typeof cur0.room === "object" ? cur0.room : {}),
      ...(r0.room && typeof r0.room === "object" ? r0.room : {}),
    },
  };
  return base;
}
